import { CheckCircle2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { FinancingResult, SimulationInput } from "../../shared/contracts";
import { sendLead } from "../lib/api";
import { currencyDetailed, systemLabel } from "../lib/format";

interface LeadDialogProps {
  open: boolean;
  onClose: () => void;
  input: SimulationInput;
  result: FinancingResult;
}

export function LeadDialog({ open, onClose, input, result }: LeadDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    dialogRef.current?.querySelector<HTMLElement>("input")?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previous?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setStatus("sending");
    setError("");
    try {
      await sendLead({
        name: String(form.get("name")),
        email: String(form.get("email")),
        phone: String(form.get("phone")),
        consent: form.get("consent") === "on",
        company: String(form.get("company") ?? ""),
        simulation: {
          input,
          selectedOption: {
            bankId: result.bankId,
            system: result.system,
            firstPayment: result.firstPayment,
            totalPaid: result.totalPaid
          }
        }
      });
      setStatus("success");
    } catch (submissionError) {
      setStatus("error");
      setError(
        submissionError instanceof Error
          ? submissionError.message
          : "Não foi possível enviar. Tente novamente."
      );
    }
  }

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="lead-dialog"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="lead-dialog-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button className="dialog-close" type="button" onClick={onClose} aria-label="Fechar">
          <X size={20} aria-hidden="true" />
        </button>

        {status === "success" ? (
          <div className="lead-success">
            <CheckCircle2 size={42} aria-hidden="true" />
            <span className="section-kicker">Estudo registrado</span>
            <h2 id="lead-dialog-title">Tudo certo.</h2>
            <p>
              Seu cenário foi associado à opção {result.bankShortName}{" "}
              {systemLabel(result.system)}. Um especialista poderá retomar exatamente destas
              premissas.
            </p>
            <button className="button button--primary" type="button" onClick={onClose}>
              Voltar à comparação
            </button>
          </div>
        ) : (
          <>
            <span className="section-kicker">Receber estudo</span>
            <h2 id="lead-dialog-title">Leve esta decisão com você.</h2>
            <p className="lead-dialog__intro">
              Cenário selecionado: <strong>{result.bankShortName}</strong> ·{" "}
              {systemLabel(result.system)} · primeira parcela de{" "}
              <strong>{currencyDetailed(result.firstPayment)}</strong>.
            </p>

            <form onSubmit={submit}>
              <label>
                <span>Nome</span>
                <input name="name" autoComplete="name" required minLength={2} maxLength={100} />
              </label>
              <label>
                <span>E-mail</span>
                <input name="email" type="email" autoComplete="email" required maxLength={160} />
              </label>
              <label>
                <span>WhatsApp / telefone</span>
                <input name="phone" type="tel" autoComplete="tel" required minLength={8} maxLength={30} />
              </label>
              <label className="honeypot" aria-hidden="true">
                Empresa
                <input name="company" tabIndex={-1} autoComplete="off" />
              </label>
              <label className="consent">
                <input name="consent" type="checkbox" required />
                <span>
                  Autorizo o uso destes dados para contato sobre esta simulação. Li a{" "}
                  <a href="/privacidade">Política de Privacidade</a>.
                </span>
              </label>
              {status === "error" && (
                <p className="form-error" role="alert">
                  {error}
                </p>
              )}
              <button className="button button--primary button--wide" type="submit" disabled={status === "sending"}>
                {status === "sending" ? "Registrando…" : "Receber estudo da simulação"}
              </button>
              <small className="form-note">
                Sem análise de crédito automática. Esta ação não envia proposta a uma instituição.
              </small>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

